import { useEffect, useMemo, useRef, useState } from 'react';
import { useCall } from './CallProvider';
import { ParticipantTile } from './Tile';
import { clock } from '../callCard';
import { useIsMobile } from '../../lib/media';
import { callTitle, Corner, cornerPosition, mainSpeaker, snapCorner, windowButtons } from './callMath';
import { IconExpand, IconMic, IconMicOff, IconPhone, IconPhoneOff } from '../../components/icons';
import type { Participant } from './callRoom';

/*
 * Свёрнутый звонок. Веб — плавающее окошко с говорящим (или показанным
 * экраном), таскается и прилипает к углу; клик по видео разворачивает.
 * Телефон — зелёная полоска под шапкой, приложение под ней работает целиком.
 */

const KEY = 'code-call-mini-corner';
const BOX = { w: 280, h: 158 };

const readCorner = (): Corner => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'tl' || v === 'tr' || v === 'bl' || v === 'br' ? v : 'br';
  } catch {
    return 'br';
  }
};

/** Кто на сцене: ушедшие и отказавшиеся звонок уже не занимают, но у
 *  завершённого звонка других и нет — тогда остаётся весь список. */
const onStage = (people: Participant[]) => {
  const live = people.filter((p) => p.status === 'active' || p.status === 'ringing');
  return live.length ? live : people;
};

export function CallMini() {
  const mobile = useIsMobile();
  return mobile ? <MiniBar /> : <MiniWindow />;
}

/**
 * Плашка «браузер не включил звук». Свёрнутый звонок молчит так же, как
 * полный, и разрешить звук нужно из жеста — значит кнопка нужна и здесь.
 */
function AudioBanner() {
  const call = useCall();
  return (
    <div className="banner info" role="status">
      <b>Браузер не включил звук</b>
      <button type="button" className="btn outline sm" onClick={() => void call.resumeAudio()}>
        Включить
      </button>
    </div>
  );
}

function MiniBar() {
  const call = useCall();
  const shown = onStage(call.participants);
  const others = shown.filter((p) => !p.isMe);
  const who = callTitle(others.map((p) => p.name));
  const text =
    call.error ||
    (call.phase === 'incoming'
      ? `${who} зовёт в звонок`
      : call.phase === 'outgoing'
        ? `${who} · вызываем…`
        : `${who} · ${clock(call.seconds)}`);

  // полоска сдвигает контент, а не накрывает его: класс на корне приложения
  useEffect(() => {
    document.getElementById('app')?.classList.add('with-call-bar');
    return () => document.getElementById('app')?.classList.remove('with-call-bar');
  }, []);

  return (
    <>
      <div className="call-bar" role="status">
        <span className="call-bar-t">{text}</span>
        {call.phase === 'incoming' ? (
          <button className="call-bar-b" onClick={() => void call.accept()}>
            <IconPhone /> Ответить
          </button>
        ) : (
          <button className="call-bar-b" onClick={call.expand}>Вернуться</button>
        )}
      </div>
      {call.audioBlocked && (
        <div className="call-bar-bars">
          <AudioBanner />
        </div>
      )}
    </>
  );
}

function MiniWindow() {
  const call = useCall();
  const [corner, setCorner] = useState<Corner>(readCorner);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [sticky, setSticky] = useState<string | null>(null);
  const start = useRef<{ px: number; py: number; x: number; y: number; moved: boolean } | null>(null);
  const bounds = () => ({ w: window.innerWidth, h: window.innerHeight });

  const shown = useMemo(() => onStage(call.participants), [call.participants]);
  const others = shown.filter((p) => !p.isMe);
  const mainId = mainSpeaker(shown, sticky);
  const main = shown.find((p) => p.id === mainId) ?? null;
  const sharer = shown.find((p) => p.screenOn) ?? null;

  useEffect(() => setSticky(mainId), [mainId]);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, corner);
    } catch {
      /* угол проживёт до перезагрузки */
    }
  }, [corner]);

  const pos = drag ?? cornerPosition(corner, BOX, bounds(), 16);
  const status = call.error || (call.phase === 'active' ? clock(call.seconds) : call.phase === 'incoming' ? 'звонит' : 'вызываем…');

  return (
    <div
      className={`call-mini${drag ? ' dragging' : ''}`}
      style={{ width: BOX.w, height: BOX.h, transform: `translate(${pos.x}px, ${pos.y}px)` }}
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        const p = cornerPosition(corner, BOX, bounds(), 16);
        start.current = { px: e.clientX, py: e.clientY, x: p.x, y: p.y, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const s = start.current;
        if (!s) return;
        const dx = e.clientX - s.px;
        const dy = e.clientY - s.py;
        if (Math.abs(dx) + Math.abs(dy) > 4) s.moved = true;
        if (s.moved) setDrag({ x: s.x + dx, y: s.y + dy });
      }}
      onPointerUp={() => {
        const s = start.current;
        if (s && !s.moved) call.expand();
        else if (drag) setCorner(snapCorner(drag.x, drag.y, BOX, bounds()));
        start.current = null;
        setDrag(null);
      }}
      onPointerCancel={() => {
        start.current = null;
        setDrag(null);
      }}
    >
      {/* в окошко влезает только одна плитка: показанный экран важнее лиц */}
      <div className="call-mini-cam">
        {sharer ? (
          <ParticipantTile p={sharer} source="screen" fit="contain" size="sm" hideName />
        ) : (
          main && <ParticipantTile p={main} size="sm" hideName />
        )}
      </div>
      {call.audioBlocked && (
        <div className="call-bars">
          <AudioBanner />
        </div>
      )}
      <div className="call-mini-f">
        <span className="call-mini-t">
          <b>{callTitle(others.map((p) => p.name))}</b>
          <i>{status}</i>
        </span>
        {/* стрелки наружу — разворот, зеркало кнопки «свернуть» из полного вида (решение владельца продукта) */}
        {windowButtons({ fullscreen: false, mini: true, isGuest: call.isGuest }).map((btn) => (
          <button key={btn} className="call-b mini" onClick={call.expand} title="Развернуть">
            <IconExpand />
          </button>
        ))}
        {call.phase === 'incoming' ? (
          <button className="call-b mini accept" onClick={() => void call.accept()} title="Ответить">
            <IconPhone />
          </button>
        ) : (
          <button className={`call-b mini${call.micOn ? '' : ' off'}`} onClick={call.toggleMic} title="Микрофон">
            {call.micOn ? <IconMic /> : <IconMicOff />}
          </button>
        )}
        <button className="call-b mini decline" onClick={() => (call.phase === 'incoming' ? call.decline() : call.hangup())} title="Завершить">
          <IconPhoneOff />
        </button>
      </div>
    </div>
  );
}
